import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ListPagination } from "@/components/ListPagination";
import { BranchCell } from "@/components/BranchFlag";
import { fmtDate } from "@/lib/format";
import { History, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { printCorrectionLog, printReleaseLog } from "@/lib/print";

const STATUSES = ["All", "Pending", "Approved", "Rejected"];
const TONE = {
  Pending: "bg-amber-100 text-amber-800 border-amber-300",
  Approved: "bg-emerald-100 text-emerald-700 border-emerald-300",
  Rejected: "bg-red-100 text-red-700 border-red-300",
};

export default function ChangeRequestLog() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("All");
  const [branch, setBranch] = useState("All");
  const [ctype, setCtype] = useState("All");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [relMonth, setRelMonth] = useState(() => new Date().toISOString().slice(0, 7));
  useEffect(() => {
    api.get("/change-requests").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  const branches = useMemo(() => [...new Set((rows || []).map((r) => r.office).filter(Boolean))].sort(), [rows]);
  const ctypes = useMemo(() => [...new Set((rows || []).map((r) => r.field_label).filter(Boolean))].sort(), [rows]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows || []).filter((r) => {
      if (status !== "All" && r.status !== status) return false;
      if (branch !== "All" && r.office !== branch) return false;
      if (ctype !== "All" && r.field_label !== ctype) return false;
      if (!needle) return true;
      return [r.lr_no, r.customer_name, r.field_label, r.requested_by, r.office, r.decided_by, r.new_value, r.old_value, r.reason]
        .some((v) => String(v || "").toLowerCase().includes(needle));
    });
  }, [rows, status, branch, ctype, q]);
  useEffect(() => setPage(1), [status, branch, ctype, q]);
  const paged = filtered.slice((page - 1) * 20, page * 20);
  const dt = (iso) => (iso ? `${fmtDate(String(iso).slice(0, 10))} ${String(iso).slice(11, 16)}` : "—");
  return (
    <div className="space-y-4" data-testid="correction-log-page">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl">Correction Log Book</h1>
        <p className="mt-1 text-sm text-muted-foreground">Every freight/invoice correction request — who asked, who decided and when.</p>
      </div>
      <Card>
        <CardContent className="p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${status === s ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                onClick={() => setStatus(s)}
                data-testid={`cr-log-filter-${s.toLowerCase()}`}
              >
                {s}
                {s !== "All" && <span className="ml-1 text-[10px]">({(rows || []).filter((r) => r.status === s).length})</span>}
              </button>
            ))}
            <select
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              data-testid="cr-log-branch-filter"
            >
              <option value="All">All Branches</option>
              {branches.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <select
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700"
              value={ctype}
              onChange={(e) => setCtype(e.target.value)}
              data-testid="cr-log-type-filter"
            >
              <option value="All">All Correction Types</option>
              {ctypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <Input className="ml-auto h-8 w-64" placeholder="Search LR / customer / staff / field" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cr-log-search" />
            <Button variant="outline" size="sm" className="h-8" onClick={() => printCorrectionLog(filtered)} data-testid="cr-log-print-btn">
              <Printer className="mr-1.5 h-4 w-4" /> Print Log
            </Button>
          </div>
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-sky-200 bg-sky-50/60 px-3 py-2">
            <span className="text-xs font-bold text-sky-800">Balance Release Log</span>
            <span className="text-[11px] text-sky-700">
              {(rows || []).filter((r) => r.field === "balance_release" && String(r.created_at || "").startsWith(relMonth)).length} approval(s) in month
            </span>
            <Input type="month" className="h-8 w-40" value={relMonth} onChange={(e) => setRelMonth(e.target.value || new Date().toISOString().slice(0, 7))} data-testid="release-log-month" />
            <Button
              variant="outline"
              size="sm"
              className="h-8 border-sky-300 text-sky-700 hover:bg-sky-100"
              onClick={() =>
                printReleaseLog(
                  (rows || []).filter((r) => r.field === "balance_release" && String(r.created_at || "").startsWith(relMonth)),
                  new Date(relMonth + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })
                )
              }
              data-testid="release-log-print-btn"
            >
              <Printer className="mr-1.5 h-4 w-4" /> Print Release Log
            </Button>
          </div>
          {rows === null ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-10 text-muted-foreground">
              <History className="h-8 w-8" />
              <p className="text-sm">No correction requests{status !== "All" ? ` with status ${status}` : ""} yet.</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    {["Requested On", "Branch", "LR No.", "Customer", "Type of Correction", "Old → New", "Reason", "Requested By", "Status", "Decided By", "Decided On"].map((h) => (
                      <TableHead key={h} className="whitespace-nowrap text-xs uppercase tracking-wide">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paged.map((r) => (
                    <TableRow key={r.id} data-testid="cr-log-row">
                      <TableCell className="whitespace-nowrap text-sm">{dt(r.created_at)}</TableCell>
                      <TableCell><BranchCell value={r.office} /></TableCell>
                      <TableCell className="text-sm font-semibold">{r.lr_no}</TableCell>
                      <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{r.field_label}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{r.old_value || "—"} → <b>{r.new_value}</b></TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm italic text-slate-500" title={r.reason}>{r.reason || "—"}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{r.requested_by}</TableCell>
                      <TableCell>
                        <span className={`whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-bold ${TONE[r.status] || ""}`}>{r.status}</span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{r.decided_by || "—"}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{dt(r.decided_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <ListPagination total={filtered.length} page={page} setPage={setPage} noun="requests" testPrefix="cr-log" />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
